import {
  DEFAULT_SELECT_MODEL_CATEGORY,
  MODEL_CATEGORY,
} from '@/app/constants/calculator';
import type { LCVTilInterest } from '@/app/types/models/lcv';
import { getLcvCategoryKey } from '@/services/lineup-model/lcv-product-compare/lcv-models-helpers';
import type {
  CategoryDefaultGrades,
  LcvModelWithFieldKey,
} from '@/services/lineup-model/lcv-product-compare/types';
import type {
  LcvModel,
  LcvModelCategory,
  LcvModelSubCategory,
} from '@/types/tis-cms/types';

export { parseLCVTilInterest } from '@/components/shared/utility/contentstack';

type TilInterestModel =
  | 'mu_x'
  | 'v_cross'
  | 'spark'
  | 'x_series'
  | 'pickup_2_door_hi_lander'
  | 'pickup_2_door_space_cab'
  | 'pickup_4_door_hi_lander'
  | 'pickup_4_door_cab4';

export const getCategoryKey = getLcvCategoryKey;

export const getVehicleTypeFromCategory = (
  category: Pick<LcvModelCategory, 'title' | 'modelGroup'>
): 'pickup' | 'ppv' => {
  if (category.modelGroup?.type === 'pickup') return 'pickup';
  if (category.modelGroup?.type === 'ppv') return 'ppv';
  return category.title === MODEL_CATEGORY.PPV_MU_X ? 'ppv' : 'pickup';
};

export const getDefaultModelCategory = (
  modelCategories: LcvModelCategory[],
  defaultCategoryKey: string = DEFAULT_SELECT_MODEL_CATEGORY
) =>
  modelCategories.find(
    (category) => getCategoryKey(category) === defaultCategoryKey
  ) ?? modelCategories[0];

const isPickupCategoryKey = (categoryKey: string) =>
  categoryKey === MODEL_CATEGORY.PUP_2_DOOR ||
  categoryKey === MODEL_CATEGORY.PUP_4_DOOR;

export const getModelSubCategoryListFromCategory = (
  modelCategoryKey: string,
  listModel: LcvModel[]
) => {
  let subModelList: LcvModelSubCategory[] = [];
  if (isPickupCategoryKey(modelCategoryKey)) {
    subModelList = listModel
      .map((model) => model.subCategory)
      .filter(
        (subCategory): subCategory is LcvModelSubCategory => !!subCategory
      );

    if (subModelList.length && typeof subModelList[0] === 'undefined')
      return [];

    subModelList = subModelList.filter(
      (a, i) =>
        subModelList?.findIndex((s) => a.documentId === s.documentId) === i
    );
    subModelList = subModelList.sort((a) =>
      a.nameEn === 'Hi-Lander' ? -1 : 1
    );
  }
  return subModelList || [];
};

export const getDefaultModelSubCategory = (
  modelCategory?: LcvModelCategory,
  listModelSubCategory?: LcvModelSubCategory[]
) => {
  if (
    !modelCategory ||
    !listModelSubCategory ||
    listModelSubCategory.length === 0
  )
    return null;

  if (isPickupCategoryKey(getCategoryKey(modelCategory))) {
    return listModelSubCategory?.[0];
  }

  return null;
};

export const resolveDefaultGradeUid = ({
  category,
  subCategory,
  models,
  defaultGrades,
  modelYear,
}: {
  category?: LcvModelCategory | null;
  subCategory?: LcvModelSubCategory | null;
  models: LcvModelWithFieldKey[];
  defaultGrades?: CategoryDefaultGrades;
  modelYear?: string;
}): string | undefined => {
  if (subCategory?.documentId) {
    const configuredDefaultId =
      defaultGrades?.defaultModelsBySubCategory?.[subCategory.documentId];
    if (
      configuredDefaultId &&
      models.some((model) => model.documentId === configuredDefaultId)
    ) {
      return configuredDefaultId;
    }

    const subCategoryModels = models.filter(
      (model) => model.subCategory?.documentId === subCategory.documentId
    );
    if (subCategoryModels[0]) {
      return subCategoryModels[0].documentId;
    }
  }

  if (
    defaultGrades?.defaultModelDocumentId &&
    models.some(
      (model) => model.documentId === defaultGrades.defaultModelDocumentId
    )
  ) {
    return defaultGrades.defaultModelDocumentId;
  }

  return models[0]?.documentId;
};

export const getTilInterestByModel = (
  modelCategory: LcvModelCategory,
  modelSubCategory: LcvModelSubCategory,
  tilVehicleModel: { [key: string]: LCVTilInterest[] }
): LCVTilInterest[] => {
  let tilInterestModel: TilInterestModel = 'mu_x';
  switch (getCategoryKey(modelCategory)) {
    case MODEL_CATEGORY.PPV_MU_X:
      tilInterestModel = 'mu_x';
      break;
    case MODEL_CATEGORY.PUP_V_CROSS:
      tilInterestModel = 'v_cross';
      break;
    case MODEL_CATEGORY.PUP_SPARK:
      tilInterestModel = 'spark';
      break;
    case MODEL_CATEGORY.PUP_X_SERIES:
      tilInterestModel = 'x_series';
      break;
    case MODEL_CATEGORY.PUP_2_DOOR:
      if (modelSubCategory) {
        switch (modelSubCategory.nameEn) {
          case 'Hi-Lander':
            tilInterestModel = 'pickup_2_door_hi_lander';
            break;
          case 'Space Cab':
          case 'Spacecab':
            tilInterestModel = 'pickup_2_door_space_cab';
            break;
        }
      }
      break;
    case MODEL_CATEGORY.PUP_4_DOOR:
      if (modelSubCategory) {
        switch (modelSubCategory.nameEn) {
          case 'Hi-Lander':
            tilInterestModel = 'pickup_4_door_hi_lander';
            break;
          case 'Cab4':
            tilInterestModel = 'pickup_4_door_cab4';
            break;
        }
      }
      break;
  }

  const TIL_INTEREST_BY_MODEL = tilVehicleModel[tilInterestModel];

  return TIL_INTEREST_BY_MODEL;
};

/**
 * Returns the lowest `down_payment_min` across all TIL interest tiers for a
 * model. Tiers are not guaranteed to be sorted by `down_payment_min`, so this
 * must not assume the first entry (`[0]`) is the minimum.
 */
export const getMinTilDownPaymentPercent = (
  tilInterestByModel?: LCVTilInterest[]
): number | undefined => {
  if (!tilInterestByModel || tilInterestByModel.length === 0) return undefined;

  return Math.min(...tilInterestByModel.map((item) => item.down_payment_min));
};

export const formatPercent = (value: string) => {
  return isNaN(parseFloat(value)) ? '' : value;
};

export const formatPayment = (value: string) => {
  return isNaN(parseInt(value)) ? '' : parseInt(value).toLocaleString();
};

export const removeFirstDot = (value: string) => {
  if (value[0] === '.') {
    value = `0${value}`;
  }
  return value;
};

export const removeEndWithDot = (value: string) => {
  if (value[value.length - 1] === '.') {
    value = value.substring(0, value.length - 1);
  }
  return value;
};

export const parseFloatAndHandleNaN = (value: string) => {
  return isNaN(parseFloat(value)) ? 0 : parseFloat(value);
};

export const removeDuplicateDot = (value: string) => {
  while (value.split('.').length > 2) {
    const lastIndex = value.lastIndexOf('.');
    value = `${value.substring(0, lastIndex)}${value.substring(lastIndex + 1)}`;
  }
  return value;
};

export const limitFloat = (value: string, decimalNumber: number) => {
  if (
    value.split('.').length === 2 &&
    value.split('.')[1].length > decimalNumber
  ) {
    let floatValue = value.split('.')[1];
    floatValue = floatValue.substr(0, decimalNumber);
    value = `${value.split('.')[0]}.${floatValue}`;
  }
  return value;
};

export const limit2Float = (value: string) => {
  if (value.split('.').length === 2 && value.split('.')[1].length > 2) {
    let floatValue = value.split('.')[1];
    floatValue = floatValue.substr(0, 2);
    value = `${value.split('.')[0]}.${floatValue}`;
  }
  return value;
};

export const getValidatedPaymentValue = (value: string, price?: string) => {
  let result = value;
  result = value.replace(/[^0-9]/, '');
  result = value.replaceAll(',', '');
  if (price && parseFloat(result) > parseFloat(price)) {
    return price.toString();
  }
  return result;
};
